export default class ServiceMap {
    private static serviceModelMapApi: any = null;
    private static serviceMap: any = null;
    private static cacheTimestamp: number = 0;
    private static readonly CACHE_DURATION = 600000; // 10 minutes
    private static ongoingRequest: Promise<any> | null = null;
    
    // Lazy load ServiceModelMap API
    private static async getServiceModelMapApi(): Promise<any> {
        if (!this.serviceModelMapApi) {
            const { default: ServiceModelMap } = await import("@/API/ServiceModelMap/ServiceModelMap");
            this.serviceModelMapApi = new ServiceModelMap();
        }
        return this.serviceModelMapApi;
    }
    
    /**
     * Get service map with intelligent caching and request deduplication
     * Multiple simultaneous calls will share the same request to prevent API spam
     */
    static async getServiceMap(): Promise<any> {
        const now = Date.now();
        
        // Return cached data if it's still valid
        if (this.serviceMap && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
            return this.serviceMap;
        }
        
        // If there's already an ongoing request, wait for it instead of making a new one
        if (this.ongoingRequest) {
            return await this.ongoingRequest;
        }
        
        // Start new request and cache the promise to prevent duplicate requests
        this.ongoingRequest = this.fetchServiceMap();
        
        try {
            const result = await this.ongoingRequest;
            this.cacheTimestamp = now;
            this.serviceMap = result;
            return result;
        } finally {
            this.ongoingRequest = null;
        }
    }
    
    private static async fetchServiceMap(): Promise<any> {
        const api = await this.getServiceModelMapApi();
        const response = await api.getAll({ perPage: 1000 });

        // Response could be an array or an object with data property
        let records: any[] = [];
        if (Array.isArray(response)) {
            records = response;
        } else if (response && Array.isArray(response.data)) {
            records = response.data;
        }

        const Models: Record<string, any> = {};

        records.forEach((item: any) => {
            const modelName = item.model;
            if (!modelName) return;

            Models[modelName] = {
                service: item.service ?? null,
                route: item.route ?? null,
                default_field: item.default_field ?? null,
                visible_fields: Array.isArray(item.visible_fields) ? item.visible_fields : [],
                map_fields: item.map_fields && typeof item.map_fields === 'object' ? item.map_fields : {},
                show_at_general_report: item.show_at_general_report ?? false,
                show_at_dashboard: item.show_at_dashboard ?? false,
                show_at_notification: item.show_at_notification ?? false,
                relations: Array.isArray(item.relations) ? item.relations : [],
            };
        });

        return { Models };
    }

    /**
     * Reset/invalidate cache - forces fresh data on next request
     */
    static resetServiceMap() {
        this.serviceMap = null;
        this.cacheTimestamp = 0;
        this.ongoingRequest = null;
    }
    
    /**
     * Check if cached data is still valid
     */
    static isCacheValid(): boolean {
        return this.serviceMap && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION;
    }
    
    /**
     * Get cached data directly without API call (returns null if cache invalid)
     */
    static getCachedData(): any {
        return this.isCacheValid() ? this.serviceMap : null;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Dynamic relation helpers (replaces hardcoded foreign-key maps)
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Find which relation in a model's relations array owns a given foreign_key.
     *
     * Example:
     *   getRelationForField('Risk', 'category_id')
     *   → { relatedModel: 'RiskCategory', defaultField: 'name', route: '/Risk', service: 'BaseService' }
     *
     * Returns null when no matching relation is found (caller should format the
     * raw value as a string instead of trying to resolve it).
     */
    static async getRelationForField(
        modelName: string,
        fieldName: string
    ): Promise<{ relatedModel: string; defaultField: string; route: string | null; service: string | null } | null> {
        try {
            const { Models } = await this.getServiceMap();

            // Search all models: look for a relation whose foreign_key matches fieldName
            // and whose "parent" model matches modelName.
            const modelEntry = Models[modelName];
            if (!modelEntry) return null;

            const relation = (modelEntry.relations as any[]).find(
                (r: any) => r.foreign_key === fieldName
            );

            if (!relation || !relation.model) return null;

            // Get the default_field for the related model
            const relatedEntry = Models[relation.model];
            const defaultField =
                relatedEntry?.default_field || relation.default_field || 'name' || 'title' || 'full_name' || 'description' || 'id';

            return {
                relatedModel: relation.model,
                defaultField,
                route: relation.route ?? relatedEntry?.route ?? null,
                service: relation.service ?? relatedEntry?.service ?? null,
            };
        } catch {
            return null;
        }
    }

    /**
     * Fully resolve a foreign-key field value to a human-readable label using
     * the ServiceModelMap relations — no hardcoded mappings needed.
     *
     * Usage in ViewLog / anywhere else:
     *   const label = await ServiceMap.resolveFieldLabel('Risk', 'category_id', '69ae....');
     *   // → "Financial Risk"  (the RiskCategory record's `name` field)
     *   // → null if the field has no relation in ServiceModelMap
     *
     * When null is returned, the caller is responsible for displaying the raw value.
     */
    static async resolveFieldLabel(
        modelName: string,
        fieldName: string,
        fieldValue: any
    ): Promise<string | null> {
        if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
            return null;
        }

        const relation = await this.getRelationForField(modelName, fieldName);
        if (!relation) return null;

        try {
            // Dynamically import the API class for the related model.
            // Convention: @/API/<ModelName>/<ModelName>
            const module = await import(`@/API/${relation.relatedModel}/${relation.relatedModel}`);
            const ApiClass = module.default;
            const api = new ApiClass();

            let record: any = null;

            if (typeof api.show === 'function') {
                record = await api.show(fieldValue);
            } else if (typeof api.getAll === 'function') {
                const list = await api.getAll({
                    select: `id|${relation.defaultField}`,
                    filter: `id|${fieldValue}`,
                });
                if (Array.isArray(list)) record = list[0];
                else if (list?.data) record = Array.isArray(list.data) ? list.data[0] : list.data;
                else record = list;
            }

            if (!record) return null;

            // Unwrap common API response shapes
            const item = Array.isArray(record) ? record[0] : record;
            const unwrapped = item?.data ? (Array.isArray(item.data) ? item.data[0] : item.data) : item;

            if (!unwrapped || typeof unwrapped !== 'object') return null;

            // Try the configured default_field first, then common fallbacks
            const fallbacks = [relation.defaultField, 'name', 'title', 'full_name', 'subject'];
            for (const f of fallbacks) {
                const val = unwrapped[f];
                if (val !== undefined && val !== null && val !== '') return String(val);
            }

            return null;
        } catch {
            return null;
        }
    }
}